import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Import the config - this will be provided at the app level
export abstract class FeatureFlagConfig {
  abstract getBaseUrl(): string;
}

/**
 * Feature Flag Service
 *
 * Loads all enabled feature keys for the current user on startup.
 * No individual API calls per feature - just checks the cached Map.
 *
 * Flow:
 * 1. On startup: GET /FeatureEvaluation/active → returns ["DELETE_STRUCTURE", "MOVE_BITTINGS", ...]
 * 2. refreshAll() can be called manually (e.g. after a supervisor edits a flag, or on component init)
 * 3. isEnabledAsync('KEY') → returns reactive Observable that emits when features load/change
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private featureCache = new Map<string, boolean>();

  // Observable for components that need to react to feature changes
  private featuresSubject = new BehaviorSubject<Map<string, boolean>>(new Map());
  public features$ = this.featuresSubject.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(FeatureFlagConfig) private config: FeatureFlagConfig
  ) {
    this.initializeFeatures();
  }

  /**
   * Check if a feature is enabled for the current user
   * Returns Observable that reacts to feature changes
   * Emits immediately with current value and again when features are loaded/refreshed
   */
  isEnabledAsync(featureKey: string, defaultValue = false): Observable<boolean> {
    return this.features$.pipe(
      map(features => features.get(featureKey.toUpperCase()) ?? defaultValue)
    );
  }

  /**
   * Manually refresh all features
   * Call this if you need immediate refresh (e.g., after supervisor action)
   */
  refreshAll(): void {
    this.initializeFeatures();
  }

  /**
   * Load all active features from API
   * Called on startup and every 30 seconds
   */
  private initializeFeatures(): void {
    const url = `${this.config.getBaseUrl()}/FeatureEvaluation/active`;
    this.http
      .get<string[]>(url)
      .pipe(catchError(() => of([])))
      .subscribe((activeKeys) => {
        // Clear and rebuild cache with only active features
        this.featureCache.clear();
        activeKeys.forEach((key) => {
          this.featureCache.set(key.toUpperCase(), true);
        });
        this.featuresSubject.next(new Map(this.featureCache));
      });
  }

}
